import BaseButton from 'components/ui/BaseButton';
import EmailInput from 'components/common/inputs/EmailInput';
import { AUTH_ACTION_TYPE } from 'enums';
import { AUTH_SCREEN } from 'constants/general';
import { PARAMS } from 'constants/params';
import PasswordInput from 'dashboard/auth/password-input';
import { useContext, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { isDesktopLoginRequest, login } from 'services/auth.service';
import { t } from 'services/i18n.service';
import { useAppSelectorShallow } from 'store/hooks';
import { onboardFlowSignInSelector } from 'store/reducer/onboard-flow/selectors';
import { AuthPageContext } from './AuthActionsWrapper';
import SignInUpWrapper from './SignInUpWrapper';
import { ROUTES } from 'constants/routes';
import {
  captureException,
  capturePostHogEvent
} from 'services/posthog.service';
import { POST_HOG_EVENT } from 'constants/events';
import { useNavigate } from 'react-router-dom';
import { checkIfRedirectNeeded } from 'utils/routes.util';
import { ATTRIB } from 'constants/test';
import { isSupportPanelPage } from 'utils/support-panel.util';

const SignIn = () => {
  const {
    state: {
      screen,
      isValidEmail,
      isValidPassword,
      email,
      password,
      isProcessing,
      googleLoginInProgress,
      appleLoginInProgress
    },
    dispatch
  } = useContext(AuthPageContext);
  const isNewUserSigninProcess = useAppSelectorShallow(
    onboardFlowSignInSelector
  );
  const navigate = useNavigate();

  const isSelected = useMemo(() => screen === AUTH_SCREEN.SIGN_IN, [screen]);
  const areCredentialsValid = useMemo(
    () => isValidEmail && isValidPassword && !isProcessing,
    [isValidEmail, isValidPassword, isProcessing]
  );

  // Track when sign-in screen is viewed
  useEffect(() => {
    if (isSelected) {
      capturePostHogEvent(POST_HOG_EVENT.SIGN_IN.VIEWED);
    }
  }, [isSelected]);

  const handleSubmit = async () => {
    capturePostHogEvent(POST_HOG_EVENT.SIGN_IN.SUBMIT_CLICKED);
    dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING, value: true });
    await login(email, password, (hasError, error) => {
      if (hasError) {
        error && toast.error(error);
        dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING });
        dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_GOOGLE_LOGIN_IN_PROGRESS });
        dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_APPLE_LOGIN_IN_PROGRESS });
        captureException(error);
        capturePostHogEvent(POST_HOG_EVENT.SIGN_IN.ERROR, {
          message: error
        });
      } else {
        capturePostHogEvent(
          isDesktopLoginRequest
            ? POST_HOG_EVENT.SIGN_IN.REDIRECT_TO_DESKTOP_SUCCESS
            : POST_HOG_EVENT.SIGN_IN.SUCCESS
        );
        const nextRoute = isDesktopLoginRequest
          ? `${ROUTES.DESKTOP_REDIRECT}?${PARAMS.SIGN_INTO_DESKTOP}=true`
          : checkIfRedirectNeeded();
        navigate(nextRoute);
      }
    });
  };

  const isAnyAuthActionInProgress = useMemo(
    () =>
      isProcessing ||
      googleLoginInProgress ||
      appleLoginInProgress ||
      isNewUserSigninProcess,
    [
      isProcessing,
      googleLoginInProgress,
      appleLoginInProgress,
      isNewUserSigninProcess
    ]
  );

  return (
    <SignInUpWrapper
      title={t('auth_page.welcome')}
      subTitle={t('auth_page.sign_in_to_focus_bear_dashboard')}
      isSelected={isSelected}>
      <form
        className='w-full flex flex-col ~gap-4/5'
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}>
        <EmailInput
          value={email}
          onChange={({ target: { value } }) =>
            dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_EMAIL, value })
          }
          hasError={!isValidEmail}
          readOnly={isAnyAuthActionInProgress}
          data-testid={ATTRIB.TEST.INPUT_EMAIL_SIGN_IN}
        />
        <PasswordInput />
        <BaseButton
          type='submit'
          title={t('auth_page.sign_in')}
          additionalStyles='py-2 shadow-md'
          disabled={isAnyAuthActionInProgress || !areCredentialsValid}
          isLoading={isProcessing || isNewUserSigninProcess}
          data-testid={ATTRIB.TEST.SIGN_IN}
        />
        {/* A new account is sent on to the app download page, which the support panel's frame cannot show. */}
        {!isSupportPanelPage() && (
          <div className='flex items-center ~gap-0.5/2 flex-wrap'>
            <p className='min-w-max text-sm text-gray-700 dark:text-secondary-dark-800'>
              {t('dont_have_an_account')}
            </p>
            <BaseButton
              variant='link'
              title={t('auth_page.sign_up')}
              onClick={() =>
                dispatch?.({ type: AUTH_ACTION_TYPE.NAVIGATE_TO_SIGN_UP })
              }
              additionalStyles='min-w-max'
              disabled={isAnyAuthActionInProgress}
              data-testid={ATTRIB.TEST.SIGN_UP}
            />
          </div>
        )}
      </form>
    </SignInUpWrapper>
  );
};

export default SignIn;
